import apiClient from '../client';

// Shapes mirror the backend Document / DocumentType / ComplianceObligation
// and are kept deliberately identical to the mobile service
// (TopOps-mobile-v1/src/api/services/document.service.ts) — one vault,
// two clients, and a field that means something different on each is the
// bug you find six months later. Dates arrive as ISO strings.
//
// NOTE: the shared apiClient has a response interceptor that returns
// response.data, so every call here resolves to the response BODY.

export type DocumentCategory =
  | 'onboarding' | 'insurance' | 'vehicle' | 'accident' | 'vedr'
  | 'isp_agreement' | 'vendor' | 'handbook' | 'job_description'
  | 'invoice' | 'vehicle_list' | 'company' | 'operations' | 'report' | 'payroll';

export type DocumentOwnerType = 'company' | 'driver' | 'vehicle' | 'vendor' | 'accident';

// A document that EXISTS. Absence is an obligation concern — see
// ObligationStatus.'missing'. Keeping the two enums separate is what stops
// "missing" ever rendering as a document that went wrong rather than one
// that was never filed.
export type DocumentStatus = 'current' | 'expiring_soon' | 'expired' | 'archived';

export type ObligationStatus = 'satisfied' | 'due_soon' | 'overdue' | 'lapsed' | 'missing';

export type DocumentSensitivity = 'normal' | 'confidential' | 'restricted';

export type DocumentRequirement = 'required' | 'recommended' | 'optional';

export interface DocumentFileRef {
  bucket: string;
  path: string;
  contentType: string;
  sizeBytes: number;
  checksum?: string;
}

export interface TopOpsDocument {
  _id: string;
  companyId: string;
  documentTypeKey: string;
  category: DocumentCategory;
  ownerType: DocumentOwnerType;
  ownerId?: string | null;
  ownerLabel?: string;
  // Generated by the backend from the type's namePattern — never typed by
  // a user.
  fileName: string;
  file: DocumentFileRef;
  issuedAt?: string;
  expiresAt?: string;
  status: DocumentStatus;
  supersedesId?: string | null;
  supersededById?: string | null;
  uploadedBy: string;
  uploadedAt: string;
  retainUntil?: string;
  legalHold: boolean;
  serviceAreaId?: number;
  notes?: string;
  deletedAt?: string | null;
}

export interface DocumentType {
  _id: string;
  key: string;
  label: string;
  category: DocumentCategory;
  ownerType: DocumentOwnerType;
  requirement: DocumentRequirement;
  sensitivity: DocumentSensitivity;
  visibleToRoles: string[];
  responsibleRole?: string;
  namePattern: string;
  expiry?: { kind: string; months?: number; month?: number; day?: number; miles?: number };
  reminderLadderDays: number[];
  retention: { months: number; anchor: string };
  regulatoryCite?: string;
}

// One row of the Needs Attention band.
export interface AttentionItem {
  _id: string;
  documentTypeKey: string;
  label: string;
  requirement: DocumentRequirement;
  ownerType: DocumentOwnerType;
  ownerId?: string | null;
  ownerLabel?: string;
  status: ObligationStatus;
  dueAt?: string | null;
  serviceAreaId?: number;
  openWorkflowInstanceId?: string | null;
}

// One row of an Entity File checklist. `hasDocument` is separate from
// `document` because a restricted row shows that it EXISTS and is current
// without ever exposing its filename.
export interface EntityFileRow {
  documentTypeKey: string;
  label: string;
  category?: DocumentCategory;
  /** Keeps every file (a week's statement, an invoice); opens as a folder. */
  folder?: boolean;
  /** The folder this type is filed in. */
  folderKey?: string | null;
  /** Current files in it — for a folder, how many it holds. */
  count?: number;
  requirement: DocumentRequirement;
  sensitivity: DocumentSensitivity;
  document: TopOpsDocument | null;
  hasDocument: boolean;
  status: ObligationStatus;
  dueAt?: string | null;
}

export interface ComplianceScore { percent: number; satisfied: number; total: number }

export interface EntityFile {
  ownerType: DocumentOwnerType;
  ownerId?: string | null;
  rows: EntityFileRow[];
  score: ComplianceScore;
}

// One row of the people list or the truck list, summarised by the server.
export interface RosterRow {
  ownerType: DocumentOwnerType;
  ownerId: string;
  label: string;
  sublabel: string | null;
  serviceAreaId: number | null;
  inactive: boolean;
  counts: Record<ObligationStatus, number>;
  // null, NOT 100, when this owner has no obligations yet. An empty set
  // means "not reconciled", and rendering it as fully compliant is the
  // one thing these screens must never do.
  score: ComplianceScore | null;
  worstStatus: ObligationStatus | null;
  tracked: boolean;
}

export interface BoardIssue {
  documentTypeKey: string;
  label: string;
  requirement: DocumentRequirement;
  status: ObligationStatus;
  dueAt: string | null;
}

export interface BoardRow {
  ownerType: DocumentOwnerType;
  ownerId: string | null;
  label: string;
  sublabel: string | null;
  serviceAreaId: number | null;
  // Worst first.
  issues: BoardIssue[];
  issueCount: number;
  worstStatus: ObligationStatus | null;
  tracked: boolean;
}

export interface ComplianceBoard {
  rows: BoardRow[];
  // What expires next anywhere in the company, so an all-clear board still
  // points at the future instead of just saying nothing is wrong.
  nextExpiry: {
    label: string; ownerLabel: string;
    ownerType: DocumentOwnerType; ownerId: string | null; dueAt: string;
  } | null;
  summary: {
    drivers: number;
    vehicles: number;
    needsAttention: number;
    totals: Record<ObligationStatus, number>;
    score: ComplianceScore | null;
  };
}

export interface CategoryCount {
  category: DocumentCategory;
  total: number;
  expiring: number;
  expired: number;
}

export interface DeletedDocument extends TopOpsDocument {
  recoverableUntil: string;
  daysLeft: number;
}

// A type the caller may actually FILE. Narrower than DocumentType: the
// server has already applied the upload gate, so anything returned here is
// safe to offer in the picker.
export interface UploadableType {
  key: string;
  label: string;
  category: DocumentCategory;
  ownerType: DocumentOwnerType;
  requirement: DocumentRequirement;
  sensitivity: DocumentSensitivity;
  // null when this kind of paper never expires (an I-9, a title). The
  // upload form hides the date field entirely rather than asking for
  // something meaningless.
  expiry: { kind: string; months?: number } | null;
  namePattern: string;
  // What people actually type — "MEC", "med card", "P&L".
  aliases?: string[];
}

export interface UploadDocumentInput {
  documentTypeKey: string;
  ownerId?: string | null;
  ownerLabel?: string;
  serviceAreaId?: number;
  issuedAt?: string;
  expiresAt?: string;
  notes?: string;
  file: File;
}

/**
 * A folder in the company's tree, served flat by the API — parentKey links
 * it up. Nothing about the tree is built into the app: regrouping Files is
 * a data change on the server.
 */
export interface FileFolder {
  key: string;
  name: string;
  parentKey: string | null;
  sortOrder: number;
  /**
   * Set when this folder holds one folder per owner — per person (driver),
   * truck (vehicle) or case (accident) — and inherited by its subfolders.
   */
  ownerType?: DocumentOwnerType | null;
  /** Types filed directly in it that this user may see. */
  typeKeys: string[];
  /** Current files in it and every folder beneath it. */
  count: number;
  /** What needs doing in it and beneath it: never filed, about to run out, run out. */
  attention?: { missing: number; expiring: number; expired: number };
}

/** A person, truck or case inside a per-owner folder. */
export interface FolderOwner {
  ownerId: string;
  ownerType: DocumentOwnerType;
  label: string;
  sublabel: string | null;
  inactive: boolean;
  /** Current files the folder holds for them. */
  count: number;
  /** What needs doing for them in this folder. The list comes worst first. */
  attention?: { missing: number; expiring: number; expired: number };
}

/** A folder inside a category, with how many current files it holds. */
export interface DocumentFolder { documentTypeKey: string; label: string; count: number }

export interface DocumentListPage {
  items: TopOpsDocument[];
  total: number;
  page: number;
  pageSize: number;
  /** Present when listing a category. */
  folders?: DocumentFolder[];
}

// The backend answers 404 for a document the caller may not see, so a
// "not found" here genuinely means "nothing to show you" and the UI never
// has to distinguish absent from forbidden.
function describe(err: any, fallback: string): string {
  return err?.response?.data?.message || fallback;
}

export class DocumentService {
  private static readonly BASE = '/api/documents';

  // The binders with their counts, for the Files home list.
  /** With an owner, the counts are that person's / truck's / case's. */
  static async getFolders(ownerId?: string): Promise<FileFolder[]> {
    try {
      const query = ownerId ? `?ownerId=${encodeURIComponent(ownerId)}` : '';
      const body = await apiClient.get<any, { folders: FileFolder[] }>(`${this.BASE}/folders${query}`);
      return body.folders || [];
    } catch (err) {
      throw new Error(describe(err, 'Could not load your folders'));
    }
  }

  static async getFolderOwners(folderKey: string, includeInactive = false): Promise<FolderOwner[]> {
    try {
      const body = await apiClient.get<any, { owners: FolderOwner[] }>(
        `${this.BASE}/folders/${encodeURIComponent(folderKey)}/owners${includeInactive ? '?includeInactive=true' : ''}`,
      );
      return body.owners || [];
    } catch (err) {
      throw new Error(describe(err, 'Could not load this folder'));
    }
  }

  static async getCategories(): Promise<CategoryCount[]> {
    try {
      const body = await apiClient.get<any, { categories: CategoryCount[] }>(`${this.BASE}/categories`);
      return body.categories || [];
    } catch (err) {
      throw new Error(describe(err, 'Could not load your file categories'));
    }
  }

  // Needs Attention — already ranked worst-first by the backend.
  static async getAttention(limit = 50): Promise<AttentionItem[]> {
    try {
      const body = await apiClient.get<any, { items: AttentionItem[] }>(`${this.BASE}/attention?limit=${limit}`);
      return body.items || [];
    } catch (err) {
      throw new Error(describe(err, 'Could not load what needs attention'));
    }
  }

  static async getBoard(): Promise<ComplianceBoard> {
    try {
      return await apiClient.get<any, ComplianceBoard>(`${this.BASE}/board`);
    } catch (err) {
      throw new Error(describe(err, 'Could not load the compliance board'));
    }
  }

  static async getRoster(ownerType: 'driver' | 'vehicle', includeInactive = false): Promise<RosterRow[]> {
    try {
      const body = await apiClient.get<any, { rows: RosterRow[] }>(
        `${this.BASE}/roster/${ownerType}?includeInactive=${includeInactive}`,
      );
      return body.rows || [];
    } catch (err) {
      throw new Error(describe(err, `Could not load the ${ownerType === 'vehicle' ? 'vehicle' : 'staff'} list`));
    }
  }

  static async getEntityFile(ownerType: DocumentOwnerType, ownerId: string | 'company'): Promise<EntityFile> {
    try {
      return await apiClient.get<any, EntityFile>(`${this.BASE}/entity/${ownerType}/${ownerId}`);
    } catch (err) {
      throw new Error(describe(err, 'Could not load this file'));
    }
  }

  static async list(params: {
    category?: DocumentCategory;
    ownerType?: DocumentOwnerType;
    ownerId?: string;
    status?: DocumentStatus;
    documentTypeKey?: string;
    /** Files filed directly in this folder. */
    folder?: string;
    search?: string;
    page?: number;
    pageSize?: number;
  } = {}): Promise<DocumentListPage> {
    try {
      const query = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') query.append(k, String(v));
      });
      return await apiClient.get<any, DocumentListPage>(`${this.BASE}?${query.toString()}`);
    } catch (err) {
      throw new Error(describe(err, 'Could not load these files'));
    }
  }

  static async getDetail(documentId: string): Promise<{ document: TopOpsDocument; type: DocumentType }> {
    try {
      return await apiClient.get<any, { document: TopOpsDocument; type: DocumentType }>(`${this.BASE}/${documentId}`);
    } catch (err) {
      throw new Error(describe(err, 'Could not open this document'));
    }
  }

  // The bytes. There is no signed URL for a document — every read is an
  // authenticated request against the API, access is re-checked on the
  // read, and sensitive reads are audited. So the browser cannot simply
  // point an <img> or <iframe> at a URL: we fetch the blob with the
  // caller's token and hand the caller an object URL, which the viewer
  // MUST revoke when it unmounts.
  static async getFileBlob(documentId: string): Promise<Blob> {
    try {
      return await apiClient.get<any, Blob>(`${this.BASE}/${documentId}/file`, { responseType: 'blob' });
    } catch (err) {
      throw new Error(describe(err, 'Could not open this file'));
    }
  }

  // Multipart upload. The backend generates the filename and refuses a
  // replacement whose expiry is not later than the one already on file, so
  // those errors are surfaced verbatim — they explain a real problem.
  // `onProgress` receives 0..1 as the bytes leave the browser.
  static async upload(
    input: UploadDocumentInput,
    onProgress?: (fraction: number) => void,
  ): Promise<TopOpsDocument> {
    try {
      const form = new FormData();
      form.append('file', input.file);
      form.append('documentTypeKey', input.documentTypeKey);
      if (input.ownerId) form.append('ownerId', input.ownerId);
      if (input.ownerLabel) form.append('ownerLabel', input.ownerLabel);
      if (input.serviceAreaId != null) form.append('serviceAreaId', String(input.serviceAreaId));
      if (input.issuedAt) form.append('issuedAt', input.issuedAt);
      if (input.expiresAt) form.append('expiresAt', input.expiresAt);
      if (input.notes) form.append('notes', input.notes);

      const body = await apiClient.post<any, { document: TopOpsDocument }>(this.BASE, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        // The client's 30s default is for JSON. A 25MB scan on a slow
        // connection outlives it and was reported as a failed upload.
        timeout: 5 * 60 * 1000,
        onUploadProgress: (event) => {
          if (onProgress && event.total) onProgress(event.loaded / event.total);
        },
      });
      return body.document;
    } catch (err) {
      throw new Error(describe(err, 'Could not file this document'));
    }
  }

  static async getUploadableTypes(): Promise<UploadableType[]> {
    try {
      const body = await apiClient.get<any, { types: UploadableType[] }>(`${this.BASE}/types`);
      return body.types || [];
    } catch (err) {
      throw new Error(describe(err, 'Could not load the list of document types'));
    }
  }

  // A browser download is the document leaving the app. On mobile this is
  // a share sheet the OS owns and we can only report what we were told; in
  // a browser we know for certain the bytes were saved, so it is always
  // recorded. Deliberately swallows its own failure — a download must not
  // be blocked because the audit call did not land.
  static async recordShared(documentId: string, via: 'download' | 'print' = 'download'): Promise<void> {
    try {
      await apiClient.post(`${this.BASE}/${documentId}/shared`, { via });
    } catch {
      // Nothing useful to say to the user about a missing audit entry.
    }
  }

  // Soft delete — recoverable for 30 days.
  static async remove(documentId: string): Promise<{ recoverableUntil: string }> {
    try {
      return await apiClient.delete<any, { recoverableUntil: string }>(`${this.BASE}/${documentId}`);
    } catch (err) {
      throw new Error(describe(err, 'Could not delete this document'));
    }
  }

  static async getRecentlyDeleted(): Promise<{ items: DeletedDocument[]; windowDays: number }> {
    try {
      return await apiClient.get<any, { items: DeletedDocument[]; windowDays: number }>(`${this.BASE}/recently-deleted`);
    } catch (err) {
      throw new Error(describe(err, 'Could not load recently deleted files'));
    }
  }

  static async restore(documentId: string): Promise<{ restored: boolean; fileName: string }> {
    try {
      return await apiClient.post<any, { restored: boolean; fileName: string }>(`${this.BASE}/${documentId}/restore`, {});
    } catch (err) {
      throw new Error(describe(err, 'Could not restore this document'));
    }
  }

  // The ZIP export streams through the API and is audited before the first
  // byte, so it is fetched as a blob with the caller's token like any other
  // read — not linked to directly.
  static async exportZip(params: {
    scope?: 'company' | 'entity';
    category?: string;
    ownerType?: DocumentOwnerType;
    ownerId?: string;
    includeArchived?: boolean;
  } = {}): Promise<Blob> {
    try {
      const query = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') query.append(k, String(v));
      });
      return await apiClient.get<any, Blob>(`${this.BASE}/export?${query.toString()}`, { responseType: 'blob' });
    } catch (err) {
      throw new Error(describe(err, 'Could not build that export'));
    }
  }
}

export default DocumentService;
